// manualDocClient — AUTHENTICATED staff client for the manual-document send
// (WEL-48 manual-doc): upload a PDF, place signature/date/initials fields on it
// by dragging in the SPA, pick the resident signers (+ optionally WHP), name the
// document, and POST it for signature.
//
// The send is a MULTIPART request (the api JSON helper can't carry a file), so we
// mirror hoasClient.uploadHoaCcr's FormData + Bearer + single 401-refresh-retry.
// Wire shapes mirror apps/api/src/routes/manualDoc.ts (payloadSchema). Keep in sync.

import { ApiError } from './api';
import { getAccessToken, refreshAccessToken } from './authService';

const API_BASE =
  import.meta.env.VITE_API_URL ?? 'https://api.applications.welcomehps.com';

/** Field types staff can place (matches MANUAL_DOC_FIELD_TYPES on the server). */
export const MANUAL_DOC_FIELD_TYPES = ['signature', 'date', 'initials'] as const;
export type ManualDocFieldType = (typeof MANUAL_DOC_FIELD_TYPES)[number];

/** A resident signer the staffer picked. Matches signerSchema on the server. */
export interface ManualDocSignerInput {
  /** AppFolio tenantId when this signer is a current resident (audit only). */
  tenantId?: string;
  firstName: string;
  lastName: string;
  email: string;
}

/**
 * One field box placed on the PDF. Coordinates are NORMALIZED 0..1 fractions of
 * the page (x,y = top-left corner; w,h = size); `page` is 1-based; `signer` is a
 * resident index (0-based) or the literal 'whp'. Matches fieldSchema on the server.
 */
export interface ManualDocFieldInput {
  type: ManualDocFieldType;
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  /** number → above the line (parallel index); { seq } → below the line
   *  (sequential index); 'whp' → the WHP countersigner. */
  signer: number | 'whp' | { seq: number };
}

/** Advisory warning codes the pre-send analysis can raise (matches
 *  AdvisoryWarningCode on the server). */
export type ManualDocAdvisoryCode = 'property_mismatch' | 'already_executed' | 'unverified';

export interface ManualDocAdvisoryWarning {
  code: ManualDocAdvisoryCode;
  /** Staff-facing one-liner. */
  message: string;
  /** Supporting lines (matched property names, pages, signals). */
  detail?: string[];
}

export interface ManualDocAnalyzeResult {
  warnings: ManualDocAdvisoryWarning[];
  info?: {
    numPages: number;
    pagesRead: number;
    ocrPages: number;
    extractedChars: number;
    verified: boolean;
    propertyChecked: boolean;
  };
}

/** A staff "Send anyway" past the advisory modal — logged server-side. */
export interface ManualDocAdvisoryOverrideInput {
  warnings: ManualDocAdvisoryCode[];
  note?: string;
}

/** The JSON `payload` part of the multipart send. Matches payloadSchema. */
export interface ManualDocSendPayload {
  documentName: string;
  documentDescription?: string;
  premises?: string;
  /** ABOVE the line — everyone emailed at once, signing in any order. */
  residents: ManualDocSignerInput[];
  /** BELOW the line — signers who go in turn, in this exact order. */
  sequential?: ManualDocSignerInput[];
  includeWhpSigner: boolean;
  fields: ManualDocFieldInput[];
  advisoryOverride?: ManualDocAdvisoryOverrideInput;
}

export interface ManualDocSendResult {
  groupId: string;
  sent: number;
  recipients: Array<{ role: string; name: string; hasEmail: boolean }>;
}

/**
 * Send a manual document for signature. `file` is the uploaded PDF; `payload`
 * carries the document name + signers + placed fields. POSTs the multipart body
 * to /api/manual-doc/send. Throws ApiError on a non-2xx (the body carries the
 * server's { error, code?, detail? }).
 */
export async function sendManualDocument(
  file: File,
  payload: ManualDocSendPayload,
): Promise<ManualDocSendResult> {
  return postMultipart<ManualDocSendResult>('/api/manual-doc/send', file, payload);
}

/**
 * PRE-SEND ADVISORY: upload the PDF + the target-tenancy context and get back
 * `{ warnings }` (wrong-property / already-executed / unverified). Advisory
 * only — a failure here must never block the send (callers treat an error as
 * "no advisory available"). Runs while staff place fields; can take tens of
 * seconds for an image-only scan (server-side OCR, hard-bounded).
 */
export async function analyzeManualDocument(
  file: File,
  context: { propertyId?: string; premises?: string },
): Promise<ManualDocAnalyzeResult> {
  return postMultipart<ManualDocAnalyzeResult>('/api/manual-doc/analyze', file, context);
}

/** Shared multipart POST (file + JSON `payload` field) with Bearer auth and the
 *  single 401-refresh-retry, mirroring hoasClient.uploadHoaCcr. */
async function postMultipart<T>(path: string, file: File, payload: unknown): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  // The JSON payload travels as a single `payload` text field (server reads
  // part.fieldname === 'payload').
  form.append('payload', JSON.stringify(payload));

  const url = `${API_BASE}${path}`;
  const send = async (token: string | null): Promise<Response> =>
    fetch(url, {
      method: 'POST',
      // NB: do NOT set Content-Type — the browser sets the multipart boundary.
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });

  let res = await send(await getAccessToken());
  if (res.status === 401 && (await refreshAccessToken())) {
    res = await send(await getAccessToken());
  }
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* empty body */ }
    throw new ApiError(res.status, body);
  }
  return (await res.json()) as T;
}
